import { type NetworkCallbacks } from './caching/persist-refresh';

/**
 * Wraps an async function with VitalStats network callbacks.
 * 
 * @param fn - The async function to wrap
 * @param callbacks - Optional network callbacks for VitalStats tracking
 * @returns A wrapped function that fires VitalStats events
 * 
 * @example
 * ```typescript
 * const wrappedFetch = withNetworkCallback(
 *   () => fetchData(),
 *   networkCallbacks
 * );
 * ```
 */
export const withNetworkCallback = <T>(
	fn: () => Promise<T>,
	callbacks?: NetworkCallbacks,
): (() => Promise<T>) => {
	if (!callbacks) {
		return fn;
	}

	return async () => {
		callbacks.onStart();
		try {
			const result = await fn();
			callbacks.onSuccess();
			return result;
		} catch (error: any) {
			const errorName = error?.name || 'UnknownError';
			const errorMessage = error?.message || 'An unknown error occurred';
			callbacks.onError(errorName, errorMessage);
			throw error;
		}
	};
};
